import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { PollResultView, RankingOption } from "./pollTypes";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { pollColorPalette } from "./utils/pollColorPalette";
import { AggregateRank, loadRankingResults, generateRankColors, formatRankLabel } from "./utils/rankingResultsUtils";
import { loadPollAggregates } from "./utils/loadPollAggregates";
import { RankingHistogram, RankingStackedDistribution } from "./visualizations";

interface PollRankingFullResultsProps {
  pollId: string;
  options: RankingOption[];
  userOrder: string[];
  hasSubmitted: boolean;
  viewMode?: PollResultView;
}

export const PollRankingFullResults = ({
  pollId,
  options,
  userOrder,
  hasSubmitted,
  viewMode,
}: PollRankingFullResultsProps) => {
  const [totals, setTotals] = useState<Record<string, AggregateRank>>({});
  const [responseCount, setResponseCount] = useState(0);
  const [actualResponseCount, setActualResponseCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<"average" | "user">("average");
  
  // Full ranking: all options must be ranked
  const displayRankCount = options.length;
  const resolvedViewMode: PollResultView = viewMode === "histogram" ? "histogram" : "stacked_distribution";

  useEffect(() => {
    const loadResults = async () => {
      try {
        // Try loading pre-calculated aggregates first
        const aggregates = await loadPollAggregates(pollId, options.length, displayRankCount);
        
        if (aggregates) {
          // Use pre-calculated data
          setTotals(aggregates.totals);
          setResponseCount(aggregates.responseCount);
          setActualResponseCount(aggregates.responseCount);
        } else {
          // Fall back to on-the-fly calculation for backward compatibility
          const data = await loadRankingResults(pollId, options.length, displayRankCount);
          setTotals(data.totals);
          setResponseCount(data.responseCount);
          setActualResponseCount(data.actualResponseCount);
        }
      } catch (error) {
        console.error("Error loading ranking results", error);
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [pollId, options.length, displayRankCount]);

  const maxRank = options.length || 1;

  const userRanks = useMemo(() => {
    const ranks = new Map<string, number>();
    userOrder.forEach((id, index) => ranks.set(id, index + 1));
    return ranks;
  }, [userOrder]);

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => {
      if (sortBy === "user") {
        const aUserRank = userRanks.get(a.id ?? "");
        const bUserRank = userRanks.get(b.id ?? "");

        if (aUserRank && bUserRank) return aUserRank - bUserRank;
        if (aUserRank) return -1;
        if (bUserRank) return 1;
      }

      const aRank = totals[a.id ?? ""]?.averageRank ?? Number.MAX_SAFE_INTEGER;
      const bRank = totals[b.id ?? ""]?.averageRank ?? Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) return aRank - bRank;
      return (a.label ?? "").localeCompare(b.label ?? "");
    });
  }, [options, totals, sortBy, userRanks]);

  // No overflow bucket for full ranking
  const rankColors = useMemo(() => generateRankColors(displayRankCount, "none"), [displayRankCount]);
  const overflowColor = pollColorPalette.lowRankOverflow;

  const histogramMaxDensity = useMemo(() => {
    return options.reduce((currentMax, option) => {
      const aggregate = totals[option.id ?? ""];
      const counts = aggregate?.counts || Array(displayRankCount).fill(0);
      const optionMax = Math.max(...counts, 0);
      return Math.max(currentMax, optionMax);
    }, 0);
  }, [displayRankCount, options, totals]);

  if (loading)
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size={120} />
      </div>
    );

  if (responseCount === 0) {
    if (actualResponseCount > 0) {
      return (
        <div className="text-center py-12">
          <p className="text-lg font-medium text-destructive">Data Error: Allocations not found</p>
          <p className="text-sm text-muted-foreground mt-2">
            {actualResponseCount} response(s) recorded but allocation data is missing.
            This may have been caused by poll configuration changes.
          </p>
        </div>
      );
    }
    return (
      <div className="text-center py-12">
        <p className="text-lg font-medium">No responses yet</p>
        <p className="text-sm text-muted-foreground mt-2">Be the first to share your ranking!</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Card className="p-4 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold">Results</h3>
            <p className="text-sm text-muted-foreground">
              Based on {responseCount} response{responseCount === 1 ? "" : "s"}
            </p>
            {!hasSubmitted && (
              <p className="text-xs text-muted-foreground mt-1">
                You are seeing public results. Submit a ranking to compare against your own order.
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <label htmlFor="rank-sort" className="text-muted-foreground whitespace-nowrap">
              Sort by
            </label>
            <select
              id="rank-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as "average" | "user")}
              className="h-9 rounded-md border bg-background px-3 text-sm"
            >
              <option value="average">Response average</option>
              <option value="user">Your ranking</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          {sortedOptions.map((option) => {
            const aggregate = totals[option.id ?? ""];
            const averageRank = aggregate?.averageRank ?? 0;
            const counts = aggregate?.counts || Array(displayRankCount).fill(0);
            const userRank = userRanks.get(option.id ?? "");
            const userRankBucketIndex = userRank ? Math.min(userRank, displayRankCount) - 1 : null;
            const optionResponseTotal =
              Math.max(aggregate?.totalResponses || 0, counts.reduce((sum, value) => sum + value, 0), 1);
            const baseTotal = optionResponseTotal;
            const averagePosition = ((averageRank - 1) / Math.max(1, maxRank - 1)) * 100;
            const histogramAveragePosition = averagePosition;
            const bucketLabels = counts.map((_, rankIndex) => formatRankLabel(rankIndex + 1));
            const histogramResponseTotal = optionResponseTotal;
            const averageLabel = averageRank ? averageRank.toFixed(1) : "—";

            return (
              <div
                key={option.id}
                className="grid grid-cols-[5rem,1fr] gap-x-4 gap-y-2 items-center p-3 rounded-lg transition"
              >
                <div className="col-span-2 flex items-start justify-between gap-2 text-left leading-tight">
                  <div className="flex items-center gap-2 text-base font-semibold min-w-0">
                    <span className="truncate">{option.label}</span>
                    {option.description && (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground border"
                            aria-label={`About ${option.label}`}
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="center" className="max-w-xs text-sm">
                          {option.description}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
                    <span className="font-semibold text-foreground">{averageLabel}</span>
                    <span>avg</span>
                  </div>
                </div>

                <div className="flex w-20">
                  <div className="w-14 h-14 rounded-md overflow-hidden flex items-center justify-center text-sm font-semibold text-muted-foreground border">
                    {option.image_url ? (
                      <img src={option.image_url} alt={option.label} className="w-full h-full object-cover" loading="lazy" width={56} height={56} />
                    ) : (
                      <span>{option.label?.charAt(0).toUpperCase() ?? "?"}</span>
                    )}
                  </div>
                </div>
                <div className="flex-1 space-y-3">
                  {resolvedViewMode === "histogram" ? (
                    <RankingHistogram
                      optionId={option.id}
                      counts={counts}
                      bucketLabels={bucketLabels}
                      rankColors={rankColors}
                      overflowColor={overflowColor}
                      userRank={userRank}
                      userRankBucketIndex={userRankBucketIndex}
                      averageRank={averageRank}
                      averagePosition={histogramAveragePosition}
                      displayRankCount={displayRankCount}
                      maxRank={maxRank}
                      responseCount={histogramResponseTotal}
                      maxDensity={histogramMaxDensity}
                      showAverageMarker={false}
                    />
                  ) : (
                    <RankingStackedDistribution
                      optionId={option.id}
                      counts={counts}
                      bucketLabels={bucketLabels}
                      rankColors={rankColors}
                      overflowColor={overflowColor}
                      userRank={userRank}
                      userRankBucketIndex={userRankBucketIndex}
                      baseTotal={baseTotal}
                      averageRank={averageRank}
                      averagePosition={averagePosition}
                      showAverageMarker={false}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-2 text-xs text-muted-foreground">
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span>{formatRankLabel(1)}</span>
              <div className="flex overflow-hidden rounded">
                {rankColors.map((color, idx) => (
                  <div key={`legend-rank-${idx}`} className="h-3 w-3" style={{ backgroundColor: color }} />
                ))}
              </div>
              <span>{formatRankLabel(displayRankCount)}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-4 rounded border-2 border-black" />
              <span>Your submission</span>
            </div>
          </div>

          <div className="space-y-1 leading-relaxed">
            <p>Average ranks count every response so scores stay comparable across all options.</p>
            <p>Use the sort control to compare community preferences against your own ranking.</p>
          </div>
        </div>
      </Card>
    </TooltipProvider>
  );
};
